import { aiSettingsToAiReplyConfig } from "@redotech/effect-api-shared/default-rules/support-settings-mapping";
import { Team, teamCanUseAi } from "@redotech/redo-team-common/team";

/** Insight spots. The last one is the fixed Teams page banner. */
export const SUPPORT_INSIGHT_SLOT_COUNT = 4;

export const supportInsightIds = [
  "ai-ticket-volume",
  "email-channel",
  "widget-ai",
  "widget-enabled",
  "social-ai",
] as const;

export type SupportInsightId = (typeof supportInsightIds)[number];

/** Platform flags use schema defaults: comment AI off, the other channels on. */
export interface SupportInsightSignals {
  aiAllowed: boolean;
  supportEnabled: boolean;
  autopilotEnabled: boolean;
  /** 0–100. Unset volume is 100, matching the assign-AI rule. */
  autopilotTicketPercent: number;
  /** Email, social, SMS, Attentive, Postscript, or TikTok AI is on. */
  anyCommunicationChannelEnabled: boolean;
  emailAiEnabled: boolean;
  widgetEnabled: boolean;
  widgetAiEnabled: boolean;
  facebookConnected: boolean;
  instagramConnected: boolean;
  facebookMessagesAiEnabled: boolean;
  facebookCommentsAiEnabled: boolean;
  instagramDmAiEnabled: boolean;
  instagramCommentsAiEnabled: boolean;
  facebookCommentsIngestEnabled: boolean;
  instagramCommentsIngestEnabled: boolean;
}

function flag(
  value: boolean | null | undefined,
  whenMissing: boolean,
): boolean {
  return value ?? whenMissing;
}

/** Converts the 0–1 ticket-volume fraction to a 0–100 percent. */
export function autopilotTicketPercent(
  volume: number | null | undefined,
): number {
  const fraction = volume ?? 1;
  return Math.round(Math.min(100, Math.max(0, fraction * 100)));
}

/** Channels the reply rule handles. Autopilot gates every channel except chat. */
function routedAiChannels(
  ai: NonNullable<Team["settings"]["support"]>["ai"] | undefined,
): ReadonlySet<string> {
  const platforms = ai?.enabledPlatforms;
  return new Set(
    aiSettingsToAiReplyConfig({
      autopilotEnabled: ai?.autopilotEnabled === true,
      automateLiveChat: ai?.automateLiveChat === true,
      enabledPlatforms: {
        email: flag(platforms?.email, true),
        facebook: flag(platforms?.facebook, true),
        facebookComments: flag(platforms?.facebookComments, false),
        instagram: flag(platforms?.instagram, true),
        instagramComments: flag(platforms?.instagramComments, false),
        sms: flag(platforms?.sms, true),
        attentive: flag(platforms?.attentive, true),
        postscript: flag(platforms?.postscript, true),
        tiktok: flag(platforms?.tiktok, true),
      },
    }).configurations.channels,
  );
}

export function supportInsightSignalsFromTeam(
  team: Team,
): SupportInsightSignals {
  const support = team.settings?.support;
  const ai = support?.ai;
  const pages = support?.metaPagesData?.pagesData ?? [];
  const routed = routedAiChannels(ai);

  return {
    aiAllowed: teamCanUseAi(team),
    supportEnabled: support?.enabled === true,
    autopilotEnabled: ai?.autopilotEnabled === true,
    autopilotTicketPercent: autopilotTicketPercent(ai?.aiAutopilotTicketVolume),
    anyCommunicationChannelEnabled: [...routed].some(
      (channel) => channel !== "redoChat",
    ),
    emailAiEnabled: routed.has("gmail"),
    widgetEnabled:
      team.settings?.customerWidget?.general?.enableInAppEmbed === true,
    widgetAiEnabled: routed.has("redoChat"),
    facebookConnected: pages.some((page) => Boolean(page.pageId)),
    instagramConnected: pages.some((page) =>
      Boolean(page.instagramBusinessAccountId),
    ),
    facebookMessagesAiEnabled: routed.has("facebook"),
    facebookCommentsAiEnabled: routed.has("facebookComments"),
    instagramDmAiEnabled: routed.has("instagram"),
    instagramCommentsAiEnabled: routed.has("instagramComments"),
    facebookCommentsIngestEnabled: flag(
      support?.facebook?.commentsEnabled,
      true,
    ),
    instagramCommentsIngestEnabled: flag(
      support?.instagram?.commentsEnabled,
      true,
    ),
  };
}

export interface SocialChannelGap {
  network: "Facebook" | "Instagram";
  channel:
    | "facebookDm"
    | "facebookComments"
    | "instagramDm"
    | "instagramComments";
}

const socialChannelRules: {
  network: SocialChannelGap["network"];
  channel: SocialChannelGap["channel"];
  open(signals: SupportInsightSignals): boolean;
}[] = [
  {
    network: "Facebook",
    channel: "facebookDm",
    open: (signals) =>
      signals.facebookConnected && !signals.facebookMessagesAiEnabled,
  },
  {
    network: "Facebook",
    channel: "facebookComments",
    open: (signals) =>
      signals.facebookConnected &&
      signals.facebookCommentsIngestEnabled &&
      !signals.facebookCommentsAiEnabled,
  },
  {
    network: "Instagram",
    channel: "instagramDm",
    open: (signals) =>
      signals.instagramConnected && !signals.instagramDmAiEnabled,
  },
  {
    network: "Instagram",
    channel: "instagramComments",
    open: (signals) =>
      signals.instagramConnected &&
      signals.instagramCommentsIngestEnabled &&
      !signals.instagramCommentsAiEnabled,
  },
];

/** Connected social channels whose AI is still off. */
export function socialChannelGaps(
  signals: SupportInsightSignals,
): SocialChannelGap[] {
  return socialChannelRules
    .filter((rule) => rule.open(signals))
    .map(({ network, channel }) => ({ network, channel }));
}

const aiUnavailable = "AI Customer Support is not available for this merchant.";

/** Why this action cannot be chosen. Undefined means the merchant qualifies. */
export function supportInsightDisabledReason(
  id: SupportInsightId,
  signals: SupportInsightSignals,
): string | undefined {
  switch (id) {
    case "ai-ticket-volume":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (!signals.autopilotEnabled) {
        return "AI Customer Support for additional communication channels is off.";
      }
      if (!signals.anyCommunicationChannelEnabled) {
        return "No communication channel is turned on for AI.";
      }
      if (signals.autopilotTicketPercent >= 100) {
        return "Every new ticket is already assigned to AI.";
      }
      return undefined;
    case "email-channel":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (signals.emailAiEnabled) {
        return "Email is already on for AI.";
      }
      return undefined;
    case "widget-ai":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (!signals.widgetEnabled) {
        return "The chat widget is off.";
      }
      if (signals.widgetAiEnabled) {
        return "AI is already on for the chat widget.";
      }
      return undefined;
    case "widget-enabled":
      if (!signals.supportEnabled) {
        return "Support is off for this merchant.";
      }
      if (signals.widgetEnabled) {
        return "The chat widget is already on.";
      }
      return undefined;
    case "social-ai":
      if (!signals.aiAllowed) {
        return aiUnavailable;
      }
      if (socialChannelGaps(signals).length > 0) {
        return undefined;
      }
      if (!signals.facebookConnected && !signals.instagramConnected) {
        return "No Facebook or Instagram page is connected.";
      }
      return "AI is already on for the connected social channels.";
  }
}

/** Insights this merchant still qualifies for, in display order. */
export function qualifySupportInsights(
  signals: SupportInsightSignals,
): SupportInsightId[] {
  return supportInsightIds.filter(
    (id) => supportInsightDisabledReason(id, signals) === undefined,
  );
}

export const supportInsightLabels: Record<SupportInsightId, string> = {
  "ai-ticket-volume": "Percent of tickets assigned to AI",
  "email-channel": "Enabling AI on email",
  "widget-ai": "Enabling AI on chat widget",
  "widget-enabled": "Enabling chat widget",
  "social-ai": "Enabling AI on social",
};

export function defaultInsightSlots(
  qualified: readonly SupportInsightId[],
): (SupportInsightId | null)[] {
  return Array.from(
    { length: SUPPORT_INSIGHT_SLOT_COUNT },
    (_, index) => qualified[index] ?? null,
  );
}

/** Drops a saved pick that no longer qualifies. Empty slots stay empty. */
export function resolveInsightSlots(
  qualified: readonly SupportInsightId[],
  overrides: readonly (SupportInsightId | null)[] | null,
): (SupportInsightId | null)[] {
  if (!overrides) {
    return defaultInsightSlots(qualified);
  }
  const used = new Set<SupportInsightId>();
  return Array.from({ length: SUPPORT_INSIGHT_SLOT_COUNT }, (_, index) => {
    const id = overrides[index];
    if (!id || !qualified.includes(id) || used.has(id)) {
      return null;
    }
    used.add(id);
    return id;
  });
}

/** Puts `id` in `index`, swapping with the slot that already shows it. */
export function assignInsightSlot(
  slots: readonly (SupportInsightId | null)[],
  index: number,
  id: SupportInsightId,
): (SupportInsightId | null)[] {
  const next = Array.from(
    { length: SUPPORT_INSIGHT_SLOT_COUNT },
    (_, slot) => slots[slot] ?? null,
  );
  const other = next.findIndex((value, slot) => slot !== index && value === id);
  const previous = next[index] ?? null;
  if (other !== -1) {
    next[other] = previous;
  }
  next[index] = id;
  return next;
}
